import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { RECORD_NOT_FOUND, useCaseRecord, useHandoverNotices, type HandoverOut } from "@/api";
import { Bone } from "@/components/Bone";
import { Button } from "@/components/Button";
import { EmptyState } from "@/components/EmptyState";
import { StateMessage } from "@/components/StateMessage";
import { useWithdrawHandover } from "@/features/handover/useWithdrawHandover";
import { acceptedFor, goneWords, newOwnerOf } from "@/lib/caseGone";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useRouteNotice } from "@/lib/useRouteNotice";

import { FactSheet } from "./FactSheet";
import { IntakeGhost } from "./FileDrop";
import { RecordHead, type RecordNotice } from "./RecordHead";
import { Timeline } from "./Timeline";
import styles from "./Record.module.css";

const UNREACHABLE =
  "Der Vorgang lässt sich gerade nicht laden. Versuch es gleich noch einmal — an der Akte ändert das nichts.";
const LOADING = "Vorgang wird geladen";

const BROKEN = "Vorgang lässt sich nicht öffnen";
const NBSP = "\u00a0";

/**
 * One line of the skeleton in the line box of the text it stands for: the
 * row carries that text's own class and a non-breaking space, so its height
 * *is* that text's height, and the bone lies over it. Pixel values measured
 * off the loaded record would drift with the next change of a token.
 */
function BoneLine({
  className,
  width,
  height,
  mono = false,
}: {
  className?: string;
  width: string;
  height?: string;
  /** The line carries a mono number (`.num`) — „Vorgang 2026-0143" — whose face sets a taller line box. */
  mono?: boolean;
}) {
  return (
    <p className={`${styles.boneLine} ${className ?? ""}`} aria-hidden="true">
      {mono ? <span className="num">{NBSP}</span> : NBSP}
      <Bone width={width} height={height} />
    </p>
  );
}

/**
 * The record's own geometry while it loads (UX ruling 5, F-5), built from
 * the classes of the loaded record rather than approximated beside it
 * (record fix wave): the fact sheet's three sections; the record column's
 * tabs with its tools, filters, a day head and three entries. The head is
 * the frame's since Task 9 and holds its own places meanwhile, so the grid
 * starts where the loaded one starts — no jump when the answer comes.
 * Static, no shimmer (CI §6).
 */
function RecordSkeleton() {
  return (
    <div className={styles.screen} role="region" aria-label={LOADING} aria-busy="true">
      <div className={styles.grid}>
        <div className={`${styles.panel} ${styles.sheet} ${styles.skeletonSheet}`}>
          <div className={styles.skeletonFolded}>
            <BoneLine className="label" width="5rem" height="0.625rem" />
            <BoneLine className={styles.summaryText} width="70%" />
          </div>
          <div className={styles.block}>
            <BoneLine className="label" width="3.5rem" height="0.625rem" />
            <BoneLine className={styles.customerName} width="75%" />
            <BoneLine className={styles.sub} width="45%" />
            <div className={styles.facts}>
              {["60%", "45%", "20%"].map((width, index) => (
                <div key={index}>
                  <BoneLine className="label" width="35%" height="0.625rem" />
                  <BoneLine className={styles.factValue} width={width} />
                </div>
              ))}
            </div>
            <span className={styles.boneQuiet} aria-hidden="true" />
          </div>
          <hr className={styles.rule} />
          <div className={styles.facts}>
            <div>
              <BoneLine className="label" width="70%" height="0.625rem" />
              <BoneLine className={styles.boneFigure} width="55%" height="1.5rem" />
              <BoneLine className={styles.sub} width="80%" />
            </div>
            {["35%", "50%", "55%"].map((width, index) => (
              <div key={index}>
                <BoneLine className="label" width="30%" height="0.625rem" />
                <BoneLine className={styles.factValue} width={width} />
              </div>
            ))}
          </div>
          <hr className={styles.rule} />
          <div className={styles.block}>
            <BoneLine className="label" width="4rem" height="0.625rem" />
            <BoneLine className={styles.sub} width="60%" />
          </div>
        </div>
        <div className={`${styles.panel} ${styles.record} ${styles.skeletonRecord}`}>
          {/* The panel's head as the loaded one: the tabs and, at their end,
              the intake's tools (Task 9). */}
          <div className={styles.skeletonTabs}>
            {["4rem", "6rem", "4.5rem"].map((width, index) => (
              <span key={index} className={styles.boneTab} aria-hidden="true">
                <Bone width={width} />
              </span>
            ))}
            <span className={styles.skeletonTools}>
              <IntakeGhost />
            </span>
          </div>
          <div className={styles.panelBody}>
            <span className={styles.boneFilters} aria-hidden="true" />
            <div className={styles.skeletonDays}>
              <BoneLine className={`label ${styles.day}`} width="7rem" height="0.625rem" />
              {Array.from({ length: 3 }, (_, index) => (
                <div key={index} className={`${styles.entry} ${styles.skeletonEntry}`}>
                  <Bone width="1.75rem" height="1.75rem" />
                  <div className={styles.boneStack}>
                    <Bone width="50%" />
                    <Bone width="85%" />
                    <Bone width="30%" height="0.625rem" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A record that cannot be shown — not found, or not reachable. The focus is
 * put here when it appears (record fix wave I3): the request that led here
 * may have come from a dialog that just closed, and its opener is gone.
 */
function RecordState({
  title,
  children,
  actions,
  empty = false,
}: {
  title: string;
  children: ReactNode;
  actions: ReactNode;
  /**
   * A Vorgang that is gone is a dead end with one way on — the empty state
   * every list has (UX gate round 4, #11). A record that could not be
   * loaded stays a notice: it is worth another try.
   */
  empty?: boolean;
}) {
  const anchor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    anchor.current?.focus();
  }, []);
  return (
    <div className={styles.screen}>
      <div ref={anchor} tabIndex={-1} role="group" aria-label={title} className={styles.stateAnchor}>
        {empty ? (
          <EmptyState title={title} actions={actions}>
            {children}
          </EmptyState>
        ) : (
          <StateMessage title={title}>
            {children}
            <div className={styles.stateActions}>{actions}</div>
          </StateMessage>
        )}
      </div>
    </div>
  );
}

/**
 * `/vorgang/:caseId` — one Vorgang as a whole (design §8.2).
 *
 * The error is read **before** the data: a Vorgang handed over while this
 * screen was open answers 404 on its next read, and TanStack keeps the old
 * record in the cache beside the error. Showing that record would show a
 * Vorgang the person no longer holds (D-1).
 *
 * „Anfrage zurückziehen" lives here (record fix wave): one request, one
 * busy state, one sentence. Its one button is the head's waiting line —
 * the fact sheet shows the request without a second one (UX gate round 3,
 * #8). Once the record comes back without the request, the focus goes to
 * „Mehr …", where „Übergeben …" is offered again (I2).
 *
 * The Vorgang's head — number, title, meta line, tabs — is the frame's since
 * Task 9 (B-1/B-4); the record fills its places with the way on, „Mehr …"
 * and the stage path (`RecordHead`, `InCaseHead`), and stands its notices,
 * the waiting line and the banner of a cancelled Vorgang under it.
 */
export function RecordScreen() {
  const [announcement, setAnnouncement] = useState("");
  // What the last withdrawal had to say, shown under the head (UX gate round 1, #3).
  const [notice, setNotice] = useState<RecordNotice | null>(null);
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const arrived = useRouteNotice();
  const record = useCaseRecord(caseId ?? null);
  // „Erneut versuchen" asked for this Vorgang and has not been answered yet
  // (record fix round 2, M-2). A query without data goes back to `pending`
  // when it is fetched again, and the error state gave way to the skeleton —
  // with the focus of the retry button on <body> for as long as it loaded.
  const [retryOf, setRetryOf] = useState<string | null>(null);
  // A record opens at its top (round-2 screen check, R2-5): the router keeps
  // the window where the last screen left it, and after „Annehmen" on a
  // scrolled start page the record came up 76 px down, its head cut off.
  // Before paint, so the scrolled frame is never shown; once per Vorgang —
  // a file dropped into the record may scroll it to the new entry later.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [caseId]);
  const retrying = retryOf !== null && retryOf === caseId && record.isFetching === true;
  const gone = record.error?.message === RECORD_NOT_FOUND;
  const shown = record.error ? null : (record.data ?? null);
  const cachedOwner = gone ? newOwnerOf(record.data) : null;
  // Asked only once the Vorgang is gone and the cache could not say where to.
  const notices = useHandoverNotices({ forGone: gone && cachedOwner === null });
  const handedTo = cachedOwner ?? (gone ? acceptedFor(caseId, notices.data) : null);
  // A 404 waits for the notices before it speaks, so the general sentence
  // does not stand for a moment before the named one replaces it.
  const askingWhere = gone && handedTo === null && notices.isFetching;
  // The same words the board and the calculation say for it (R5-6).
  const goneState = goneWords(handedTo);
  const goneTitle = goneState.title;
  const withdrawal = useWithdrawHandover(caseId ?? "", shown?.case.title ?? "");
  // „Mehr …" in the Vorgang's head, where „Übergeben …" lives (Task 9).
  const moreRef = useRef<HTMLButtonElement>(null);
  const refocusHandover = useRef(false);
  const pendingId = shown?.handover?.id ?? null;
  useDocumentTitle(
    shown !== null
      ? `${shown.case.caseNo} · ${shown.case.title}`
      : (record.isPending && !retrying) || askingWhere
        ? LOADING
        : gone
          ? goneTitle
          : BROKEN,
  );

  useEffect(() => {
    if (!refocusHandover.current || pendingId !== null) return;
    refocusHandover.current = false;
    moreRef.current?.focus();
  }, [pendingId]);

  function withdraw(open: HandoverOut) {
    if (withdrawal.busy) return;
    refocusHandover.current = true;
    setNotice(null);
    void withdrawal.withdraw(open).then(({ sentence, closed, tone }) => {
      // Still open (the server was not reached): the focus stays where it is.
      if (!closed) refocusHandover.current = false;
      if (sentence === "") return;
      setAnnouncement(sentence);
      setNotice({ tone, text: sentence });
    });
  }

  function retry() {
    if (retrying || caseId === undefined) return;
    setRetryOf(caseId);
    void record.refetch().finally(() => setRetryOf(null));
  }

  if (!caseId) return <Navigate to="/vorgaenge" replace />;
  if ((record.isPending && !retrying) || askingWhere) return <RecordSkeleton />;
  if (shown === null) {
    // The same state stays mounted through a retry — the button keeps the
    // focus and says „Wird geladen …" until the record is there (M-2). A
    // retry that finds the Vorgang gone is a new state, with its own anchor.
    return (
      <RecordState
        key={gone ? "gone" : "broken"}
        title={gone ? goneTitle : BROKEN}
        empty={gone}
        actions={
          <>
            {gone ? null : (
              <Button
                variant="primary"
                busy={retrying}
                aria-disabled={retrying ? "true" : undefined}
                onClick={retry}
              >
                {retrying ? "Wird geladen …" : "Erneut versuchen"}
              </Button>
            )}
            <Button variant={gone ? "primary" : "secondary"} onClick={() => navigate("/vorgaenge")}>
              Zum Register
            </Button>
          </>
        }
      >
        <p className={styles.stateText}>
          {gone ? (arrived ?? goneState.text) : UNREACHABLE}
        </p>
      </RecordState>
    );
  }

  return (
    <div className={styles.screen} key={caseId}>
      <RecordHead
        record={shown}
        arrived={arrived}
        announcement={announcement}
        onAnnounce={setAnnouncement}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        withdrawing={withdrawal.busy}
        onWithdraw={withdraw}
        moreRef={moreRef}
      />
      <div className={styles.grid}>
        <FactSheet record={shown} contact={shown.contact} />
        <Timeline caseId={caseId} record={shown} />
      </div>
    </div>
  );
}
